import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Page,
  Card,
  PageActions,
  TextField,
  FormLayout,
  Toast,
  Loading,
  Checkbox,
  Thumbnail,
  DropZone,
  InlineError,
  Text,
  Select,
  Stack,
  Button,
  ButtonGroup,
  OptionList,
  Spinner,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import JoditEditor from "jodit-react";
import LoadingScreen from "../../../Components/LoadingScreen";
import { MultiSelect } from "react-multi-select-component";

const AddEditProjectMindset = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [facilities, setFacilities] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isBrochureUploading, setIsBrochureUploading] = useState(false);
  const [isFactsheetUploading, setIsFactsheetUploading] = useState(false);
  const [fileError, setFileError] = useState("");

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      toolbarButtonSize: "middle",
      buttons: [
        "bold",
        "italic",
        "underline",
        "link",
        "ul",
        "ol",
        "fullsize",
        "source",
      ],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      enableDragAndDropFileToEditor: false,
      spellcheck: false,
      askBeforePasteHTML: false,
      placeholder: "",
    }),
    []
  );

  const [item, setItem] = useState({
    name: "",
    location: "",
    status: "",
    delivery_date: "",
    unit_mix: "",
    bedrooms: "",
    bathrooms: "",
    parking: "",
    common_area: "",
    storage: "",
    balconies: "",
    total_area: "",
    overview: "",
    video_link: "",
    brochure_link: "",
    factsheet: "",
    image: "",
    google_map_url: "",
    is_active: true,
    apartments: "",
    logo: "",
    sector: "",
  });

  const [errors, setErrors] = useState({});
  const imageKey = useRef("");
  const imageLogoKey = useRef("");
  const brochureKey = useRef("");
  const factsheetKey = useRef("");

  const validFileTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
  ];
  const handleImageDropLogo = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0], "logo");
  }, []);
  // 📸 --- Image Upload ---
  const handleImageDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0], "image");
  }, []);

  // 📄 --- Brochure Upload ---
  const handleBrochureDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0], "brochure");
  }, []);

  // 📊 --- Factsheet Upload ---
  const handleFactsheetDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0], "factsheet");
  }, []);

  // 🧾 Shared file upload logic
  const sendFile = (file, type) => {
    const form_data = new FormData();
    form_data.append("entity_type", "project");
    form_data.append("image", file);

    const setUploading =
      type === "image" || type === "logo"
        ? setIsImageUploading
        : type === "brochure"
        ? setIsBrochureUploading
        : setIsFactsheetUploading;
    setUploading(true);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        const key = result.data.data.file_key;
        if (type === "image") {
          imageKey.current = key;
          setItem((prev) => ({
            ...prev,
            image: window.URL.createObjectURL(file),
          }));
        } else if (type === "logo") {
          imageLogoKey.current = key;
          setItem((prev) => ({
            ...prev,
            logo: window.URL.createObjectURL(file),
          }));
        } else if (type === "brochure") {
          brochureKey.current = key;
          setItem((prev) => ({
            ...prev,
            brochure_link:
              "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304",
          }));
        } else {
          factsheetKey.current = key;
          setItem((prev) => ({
            ...prev,
            factsheet:
              "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304",
          }));
        }
      })
      .finally(() => setUploading(false))
      .catch((err) => console.error(err));
  };

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = () => setActive((a) => !a);

  const toastMarkup = active ? (
    <Toast content="Changes saved successfully" onDismiss={toggleActive} />
  ) : null;

  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    if (!item.name.trim()) {
      newErrors.name = "Name is required";
      hasError = true;
    }
    if (!item.image) {
      setFileError("Please upload an image");
      hasError = true;
    }
    if (!item.location.trim()) {
      newErrors.location = "Location is required";
      hasError = true;
    }
    if (!item.sector === "") {
      newErrors.sector = "Sector is required";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  // 🧠 Load facilities list & existing project data if edit
  useEffect(() => {
    axios
      .get(`/facilities`)
      .then((res) => {
        const list =
          res.data?.data?.data?.map((a) => ({
            label: a.name,
            value: a.id.toString(),
          })) || [];
        setFacilities(list);
      })
      .catch(console.error);

    axios
      .get(`/sectors`)
      .then((res) => {
        const list =
          res.data?.data?.data?.map((a) => ({
            label: a.name,
            value: a.id.toString(),
          })) || [];
        setSectors(list);
      })
      .catch(console.error);

    if (mode === "edit") {
      axios
        .get(`/projects/${id}`)
        .then((res) => {
          const d = res.data.data;
          console.log("data=", d);
          setItem({
            name: d.name || "",
            location: d.location || "",
            status: d.status || "",
            delivery_date: d.delivery_date || "",
            unit_mix: d.unit_mix || "",
            bedrooms: d.bedrooms || "",
            bathrooms: d.bathrooms || "",
            parking: d.parking || "",
            common_area: d.common_area || "",
            storage: d.storage || "",
            balconies: d.balconies || "",
            total_area: d.total_area || "",
            overview: d.overview || "",
            video_link: d.video_url || "",
            apartments: d.apartments || "",
            sector: d.sector_id !== null ? String(d.sector_id) : "",
            brochure_link: d.brochure
              ? "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304"
              : "",
            factsheet: d.factsheet
              ? "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304"
              : "",
            image: d.image_url || "",
            logo: d.logo_url || "",
            google_map_url: d.google_map_location_url || "",
            is_active: d.is_active ? true : false,
          });
          d.facilities?.length &&
            setSelectedFacilities(
              d.facilities.map((item) => ({
                label: item.name,
                value: String(item.id),
              }))
            );

          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, []);

  // 💾 Save handler
  function handleSave() {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("name", item.name);
    formData.append("location", item.location);
    formData.append("status", item.status);
    formData.append("delivery_date", item.delivery_date);
    formData.append("unit_mix", item.unit_mix);
    formData.append("bedrooms", item.bedrooms);
    formData.append("bathrooms", item.bathrooms);
    formData.append("parking", item.parking);
    formData.append("common_area", item.common_area);
    formData.append("apartments", item.apartments);
    formData.append("storage", item.storage);
    formData.append("balconies", item.balconies);
    formData.append("total_area", item.total_area);
    formData.append("overview", item.overview);
    formData.append("video_url", item.video_link);
    formData.append("google_map_location_url", item.google_map_url);
    formData.append("is_active", item.is_active ? 1 : 0);
    formData.append("sector_id", item.sector);
    if (imageKey.current) formData.append("image", imageKey.current);
    if (imageLogoKey.current) formData.append("logo", imageLogoKey.current);
    if (brochureKey.current) formData.append("brochure", brochureKey.current);
    if (factsheetKey.current)
      formData.append("factsheet", factsheetKey.current);

    selectedFacilities?.length &&
      selectedFacilities.forEach((selected) =>
        formData.append("facilities[]", selected.value)
      );

    axios
      .post(mode === "new" ? `/projects` : `/projects/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate(`/admin/projects-mindset`) : toggleActive();
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  }

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Project`}
      breadcrumbs={[{ onAction: () => navigate(`/admin/projects-mindset`) }]}
    >
      {isSaving && <Loading />}
      <Card sectioned>
        <FormLayout>
          <Select
            label="Sector"
            options={sectors}
            value={item.sector}
            onChange={(v) => handleChange("sector", v)}
            placeholder="Please select"
          />
          {/* --- Image --- */}
          <Text>Image</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image || ""} />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept=".png,.jpeg,.jpg,.webp"
                allowMultiple={false}
                onDrop={handleImageDrop}
              >
                <DropZone.FileUpload actionHint="Upload an image" />
              </DropZone>
            </div>
          </div>

          {fileError && <InlineError message={fileError} />}

          <Text>Logo</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.logo || ""} />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept=".png,.jpeg,.jpg,.webp,.svg"
                allowMultiple={false}
                onDrop={handleImageDropLogo}
              >
                <DropZone.FileUpload actionHint="Upload an image" />
              </DropZone>
            </div>
          </div>

          {/* --- Basic Info --- */}
          <TextField
            label="Name"
            value={item.name}
            onChange={(v) => handleChange("name", v)}
            error={errors.name}
          />
          <TextField
            label="Location"
            value={item.location}
            onChange={(v) => handleChange("location", v)}
            error={errors.location}
          />
          <Select
            label="Status"
            options={[
              { label: "Planning Phase", value: "planned" },
              {
                label: "Construction Phase Is Starting Soon",
                value: "construction",
              },
              { label: "Under Development", value: "under_development" },
              { label: "Delivered", value: "delivered" },
            ]}
            value={item.status}
            onChange={(v) => handleChange("status", v)}
            placeholder="Please select"
          />
          <TextField
            label="Delivery Date"
            type="date"
            value={item.delivery_date}
            onChange={(v) => handleChange("delivery_date", v)}
          />
          <TextField
            label="Apartments"
            value={item.apartments}
            onChange={(v) => handleChange("apartments", v)}
          />

          {/* --- Numeric Fields --- */}
          <TextField
            label="Unit Mix"
            value={item.unit_mix}
            onChange={(v) => handleChange("unit_mix", v)}
          />
          <TextField
            label="Bedrooms"
            // type="number"
            value={item.bedrooms}
            onChange={(v) => handleChange("bedrooms", v)}
          />
          <TextField
            label="Bathrooms"
            // type="number"
            value={item.bathrooms}
            onChange={(v) => handleChange("bathrooms", v)}
          />
          <TextField
            label="Parking"
            value={item.parking}
            onChange={(v) => handleChange("parking", v)}
          />
          <TextField
            label="Common Area"
            value={item.common_area}
            onChange={(v) => handleChange("common_area", v)}
          />
          <TextField
            label="Storage"
            value={item.storage}
            onChange={(v) => handleChange("storage", v)}
          />
          <TextField
            label="Balconies/Terraces"
            value={item.balconies}
            onChange={(v) => handleChange("balconies", v)}
          />
          <TextField
            label="Total Area"
            value={item.total_area}
            onChange={(v) => handleChange("total_area", v)}
          />

          {/* --- Overview (rich text) --- */}
          <Text>Overview</Text>
          <JoditEditor
            value={item.overview}
            onChange={(v) => handleChange("overview", v)}
            config={editorConfig}
          />

          {/* --- Other Links --- */}
          <TextField
            label="Video Link"
            value={item.video_link}
            onChange={(v) => handleChange("video_link", v)}
          />
          <TextField
            label="Google Map Location URL"
            value={item.google_map_url}
            onChange={(v) => handleChange("google_map_url", v)}
          />
          {/* --- Facilities --- */}
          <Text>Facilities</Text>
          <div style={{ position: "relative", zIndex: 9999 }}>
            <MultiSelect
              options={facilities}
              onChange={setSelectedFacilities}
              value={selectedFacilities}
              placeholder="Please select"
            />
          </div>

          {/* --- Brochure Upload --- */}
          <Text>Brochure (PDF)</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.brochure_link || ""} />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept=".png,.jpeg,.jpg,.webp,.pdf"
                allowMultiple={false}
                onDrop={handleBrochureDrop}
              >
                <DropZone.FileUpload actionHint="Upload brochure file" />
              </DropZone>
            </div>
          </div>

          {/* {item.brochure_link && (
            <Text variant="bodySm">{item.brochure_link}</Text>
          )} */}

          {/* --- Factsheet Upload --- */}
          <Text>Factsheet (PDF)</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.factsheet || ""} />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept=".png,.jpeg,.jpg,.webp,.pdf"
                allowMultiple={false}
                onDrop={handleFactsheetDrop}
              >
                <DropZone.FileUpload actionHint="Upload factsheet file" />
              </DropZone>
            </div>
          </div>

          {/* {item.factsheet && <Text variant="bodySm">{item.factsheet}</Text>} */}

          {/* --- Active toggle --- */}
          <Checkbox
            label="Is active"
            checked={item.is_active}
            onChange={(v) => handleChange("is_active", v)}
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              onAction: () => {
                setIsSaving(true);
                axios
                  .delete(`/projects/${id}`)
                  .then(() => navigate(`/admin/projects-mindset`))
                  .finally(() => setIsSaving(false));
              },
            },
          ]
        }
      />

      {toastMarkup}
      {(isImageUploading || isBrochureUploading || isFactsheetUploading) && (
        <LoadingScreen />
      )}
    </Page>
  );
};

export default AddEditProjectMindset;
